import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  Lock, Globe, Eye, Code2, Trash2, Link2, Archive, Pen,
  CheckCircle2, RefreshCw, Store,
} from 'lucide-react';

// One card in the "Recent apps" grid.
//
// Lifted out of AIBuilderSection so the dashboard's Home can show the same card
// the marketing page does — the point of the dashboard is that signing in does
// not feel like arriving somewhere else, and two hand-built grids would drift
// apart the first time either changed.
//
// Purely presentational: every action is a callback, so the homepage can open
// its preview modal while the dashboard routes instead.

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function RecentAppCard({ site, isPaid, onDelete, onPreview, onGetCode, onEdit, onRename, onTogglePublic, onCopyLink, onExportZip, onSell = null }) {
  const [hovering, setHovering] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState(site.title || '');
  const titleInputRef = useRef(null);

  useEffect(() => { if (renaming) titleInputRef.current?.focus(); }, [renaming]);
  useEffect(() => { setNewTitle(site.title || ''); }, [site.title]);

  // Ready = there is a site to show. See the status badge below.
  const ready = site.status === 'completed' || String(site.html_content || '').trim().length > 0;

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${site.title || 'this website'}"? This cannot be undone.`)) return;
    setDeleting(true);
    await onDelete(site.id);
    setDeleting(false);
  };

  const commitRename = () => {
    const trimmed = newTitle.trim();
    if (trimmed && trimmed !== site.title) onRename(site.id, trimmed);
    setRenaming(false);
  };

  const handleRenameKey = (e) => {
    if (e.key === 'Enter')  commitRename();
    if (e.key === 'Escape') { setNewTitle(site.title || ''); setRenaming(false); }
  };

  return (
    <div
     
     
     
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className="group relative rounded-2xl bg-zinc-900 border border-zinc-800 overflow-hidden transition-all duration-200 hover:border-zinc-700 hover:shadow-[0_0_30px_rgba(0,0,0,0.5)]"
    >
      {/* Preview thumbnail */}
      <div className="h-44 bg-zinc-800 relative overflow-hidden">
        {site.html_content ? (
          <iframe
            title={site.title}
            srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><script src="https://cdn.tailwindcss.com"></script><style>${site.css_content || ''}</style></head><body style="pointer-events:none;overflow:hidden;">${site.html_content}</body></html>`}
            className="w-full h-full border-none scale-[0.5] origin-top-left"
            style={{ width: '200%', height: '200%', pointerEvents: 'none' }}
            sandbox="allow-scripts"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Globe className="w-10 h-10 text-zinc-700" />
          </div>
        )}

        {/* Status badge. A site with content is ready whatever the status
            column says: a failed edit used to mark a finished site 'failed'
            (and one cut off mid-run left it 'generating'), though nothing in
            it had changed. A first build in progress has no content yet. */}
        {site.status === 'generating' && !ready && (
          <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-orange-500/20 border border-orange-500/30 text-orange-400 text-xs">
            <RefreshCw className="w-3 h-3 animate-spin" /> Generating
          </div>
        )}
        {ready && (
          <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs">
            <CheckCircle2 className="w-3 h-3" /> Ready
          </div>
        )}

        {/* Hover overlay */}
        <AnimatePresence>
          {hovering && (
            <div
             
             
             
              className="absolute inset-0 bg-zinc-950/90 flex items-center justify-center gap-2 p-3"
            >
              <button
                onClick={() => onPreview(site)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white text-zinc-950 text-xs font-semibold hover:bg-zinc-100 transition-colors"
              >
                <Eye className="w-3.5 h-3.5" /> Preview
              </button>
              <button
                onClick={() => onGetCode(site)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                  isPaid
                    ? 'bg-orange-500 hover:bg-orange-400 text-ink'
                    : 'bg-zinc-800 text-zinc-400 cursor-not-allowed'
                }`}
              >
                {isPaid ? <Code2 className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                {isPaid ? 'Code' : 'Locked'}
              </button>
              <button
                onClick={() => onEdit(site)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-zinc-700 hover:bg-zinc-600 text-white text-xs font-semibold transition-colors"
              >
                 Edit
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-500/20 text-red-400 text-xs font-semibold hover:bg-red-500/30 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Card info */}
      <div className="p-4">
        {renaming ? (
          <div className="flex items-center gap-1.5 mb-1">
            <input
              ref={titleInputRef}
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              onKeyDown={handleRenameKey}
              onBlur={commitRename}
              className="flex-1 bg-zinc-800 border border-zinc-600 rounded-lg px-2 py-0.5 text-white text-sm outline-none focus:border-orange-500 min-w-0"
            />
          </div>
        ) : (
          <button
            onClick={() => setRenaming(true)}
            className="w-full text-left group/title mb-1"
            title="Click to rename"
          >
            <h3 className="text-white font-semibold text-sm truncate flex items-center gap-1.5">
              {site.title || 'Untitled Website'}
              <Pen className="w-2.5 h-2.5 text-zinc-600 opacity-0 group-hover/title:opacity-100 shrink-0 transition-opacity" />
            </h3>
          </button>
        )}

        {site.prompt && (
          <p className="text-zinc-500 text-xs line-clamp-2 mb-2 leading-relaxed">{site.prompt}</p>
        )}
        <p className="text-zinc-600 text-xs mb-3">{formatDate(site.created_at)}</p>

        {/* Action row */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => onTogglePublic(site)}
            title={site.is_public ? 'Make Private' : 'Make Public'}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
              site.is_public
                ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'
                : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
            }`}
          >
            {site.is_public ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
            {site.is_public ? 'Public' : 'Private'}
          </button>
          <button
            onClick={() => onCopyLink(site)}
            aria-label="Copy share link"
            className="p-1.5 rounded-lg bg-zinc-800 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700 transition-colors"
          >
            <Link2 className="w-3 h-3" />
          </button>
          <button
            onClick={() => onExportZip(site)}
            aria-label={isPaid ? 'Export as ZIP' : 'Upgrade to export'}
            className={`p-1.5 rounded-lg transition-colors ${
              isPaid
                ? 'bg-zinc-800 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700'
                : 'bg-zinc-800 text-zinc-700 cursor-not-allowed'
            }`}
          >
            <Archive className="w-3 h-3" />
          </button>
          {/* Only where the card's owner can list it — the dashboard. A site
              with nothing in it has nothing to sell. */}
          {onSell && ready && (
            <button
              onClick={() => onSell(site)}
              title="Sell this site on the marketplace"
              className="ml-auto flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 transition-colors"
            >
              <Store className="w-3 h-3" /> Sell
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
